import React from "react";
import { observer } from "mobx-react-lite";
import {
    Label,
    PrimaryButton,
    DefaultButton,
    SpinButton,
    Separator,
    ActionButton,
    mergeStyleSets,
    ILabelStyles,
    ISpinButtonStyles,
} from "@fluentui/react";

import { AppState } from "../../state/AppState";
import { useAppState } from "../../contexts/StateContext";
import { IThrowOutQuestionDialogState } from "../../state/IThrowOutQuestionDialogState";
import { ModalVisibilityStatus } from "../../state/ModalVisibilityStatus";
import { ModalDialog } from "./ModalDialog";

export const ThrowOutQuestionDialog = observer(function ThrowOutQuestionDialog(): JSX.Element {
    const appState: AppState = useAppState();
    const dialogState: IThrowOutQuestionDialogState | undefined = appState.uiState.dialogState.throwOutQuestionDialog;

    const [replacementNumber, setReplacementNumber] = React.useState<number | undefined>(
        dialogState?.defaultReplacementNumber
    );
    const [showCustomInput, setShowCustomInput] = React.useState(false);

    // Sync local state when dialog opens with a new default
    const prevDefault = React.useRef(dialogState?.defaultReplacementNumber);
    if (prevDefault.current !== dialogState?.defaultReplacementNumber) {
        prevDefault.current = dialogState?.defaultReplacementNumber;
        setReplacementNumber(dialogState?.defaultReplacementNumber);
        setShowCustomInput(false);
    }

    if (dialogState == undefined) {
        return <></>;
    }

    const closeHandler = (): void => hideDialog(appState);

    const confirmHandler = (): void => {
        const replacementIndex: number | undefined = getReplacementIndexForConfirmation(
            replacementNumber,
            showCustomInput,
            dialogState.defaultReplacementIsExplicit
        );
        dialogState.onConfirm(replacementIndex);
        hideDialog(appState);
    };

    const customInputSection: JSX.Element =
        dialogState.defaultReplacementNumber != undefined ? (
            <SpinButton
                label="Replacement question number"
                min={dialogState.minQuestionNumber}
                max={dialogState.maxQuestionNumber}
                value={replacementNumber?.toString() ?? ""}
                onValidate={(value) => {
                    const parsed = parseInt(value, 10);
                    if (
                        !isNaN(parsed) &&
                        parsed >= dialogState.minQuestionNumber &&
                        parsed <= dialogState.maxQuestionNumber
                    ) {
                        setReplacementNumber(parsed);
                        return parsed.toString();
                    }
                    return (replacementNumber ?? dialogState.defaultReplacementNumber)?.toString() ?? "";
                }}
                onIncrement={(value) => {
                    const parsed = parseInt(value, 10);
                    const next = Math.min(
                        (isNaN(parsed) ? dialogState.minQuestionNumber : parsed) + 1,
                        dialogState.maxQuestionNumber
                    );
                    setReplacementNumber(next);
                    return next.toString();
                }}
                onDecrement={(value) => {
                    const parsed = parseInt(value, 10);
                    const prev = Math.max(
                        (isNaN(parsed) ? dialogState.minQuestionNumber : parsed) - 1,
                        dialogState.minQuestionNumber
                    );
                    setReplacementNumber(prev);
                    return prev.toString();
                }}
                styles={spinButtonStyles}
            />
        ) : (
            <Label styles={noReplacementLabelStyles}>
                No replacement available — upload additional questions before confirming.
            </Label>
        );

    return (
        <ModalDialog
            title={dialogState.title}
            visibilityStatus={ModalVisibilityStatus.ThrowOutQuestion}
            maxWidth="30vw"
            onDismiss={closeHandler}
        >
            <Label>{dialogState.message}</Label>
            <Separator />
            <ActionButton
                iconProps={{ iconName: showCustomInput ? "ChevronDown" : "ChevronRight" }}
                onClick={() => setShowCustomInput(!showCustomInput)}
                className={classNames.toggleButton}
            >
                Use a different replacement question
            </ActionButton>
            {showCustomInput && customInputSection}
            <div className={classNames.buttonRow}>
                <PrimaryButton text="Confirm" onClick={confirmHandler} />
                <DefaultButton text="Cancel" onClick={closeHandler} />
            </div>
        </ModalDialog>
    );
});

function hideDialog(appState: AppState): void {
    appState.uiState.dialogState.hideThrowOutQuestionDialog();
}

export function getReplacementIndexForConfirmation(
    replacementNumber: number | undefined,
    showCustomInput: boolean,
    defaultReplacementIsExplicit: boolean
): number | undefined {
    if (replacementNumber == undefined) {
        return undefined;
    }

    if (!showCustomInput && !defaultReplacementIsExplicit) {
        return undefined;
    }

    return replacementNumber - 1;
}

const classNames = mergeStyleSets({
    buttonRow: {
        display: "flex",
        gap: 8,
        marginTop: 16,
        marginBottom: 8,
    },
    toggleButton: {
        display: "block",
        height: 24,
        padding: 0,
        textAlign: "left",
    },
});

const spinButtonStyles: Partial<ISpinButtonStyles> = { root: { marginTop: 8 } };

const noReplacementLabelStyles: Partial<ILabelStyles> = {
    root: { marginTop: 8, fontStyle: "italic" },
};
